// ReportManagement.tsx
import { fetchReportManagement, sendWarningEmails } from 'apis/adminServices/Report&fFeedbackServices';
import React, { useState, useEffect } from 'react';
import { useToastStore } from 'store/toastStore';
import Filter from './reportFilter';

import ReportDetailsModal from './ReportDetailsModel';
import SearchBar from 'pages/Admin/creditsManagement/searchbar';
import Pagination from 'pages/Admin/creditsManagement/pagination';


interface Report {
  reportManagementId: number;
  requestedBy: string;
  requestedDate: string;
  answeredBy: string;
  answeredDate: string;
  requesterEmail: string;
  responderEmail: string;
  flightNumber: string;
  isWarningSent: boolean;
}

interface Recipient {
  reportManagementId: number;
  name: string;
  email: string;
  flightNumber: string;
}

const ReportManagement: React.FC = () => {
  const { showToast } = useToastStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null); // Track the report for the modal
  const [reports, setReports] = useState<Report[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalReports, setTotalReports] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [hasMorePages, setHasMorePages] = useState<boolean>(true);
  const [requestedFromDate, setRequestedFromDate] = useState<string | undefined>(undefined);
  const [requestedToDate, setRequestedToDate] = useState<string | undefined>(undefined);
  const [answeredFromDate, setAnsweredFromDate] = useState<string | undefined>(undefined);
  const [answeredToDate, setAnsweredToDate] = useState<string | undefined>(undefined);
  const [selectedReports, setSelectedReports] = useState<number[]>([]);
  const [sendingWarningIds, setSendingWarningIds] = useState<Set<number>>(new Set());
  const [sentWarningIds, setSentWarningIds] = useState<Set<number>>(new Set());
  const [sendingBulkWarning, setSendingBulkWarning] = useState(false);

  const [pageSize, setPageSize] = useState(10);

  // Reset page to 1 when search term or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, requestedFromDate, requestedToDate, answeredFromDate, answeredToDate]);

  useEffect(() => {
    const loadReports = async () => {
      setLoading(true);
      setSentWarningIds(new Set()); // Reset sent warnings when reports reload
      try {
        const result = await fetchReportManagement({
          pageNumber: currentPage,
          pageSize: pageSize,
          nameOrEmail: searchTerm?.trim()?.toLowerCase() || undefined,
          requestedFromDate,
          requestedToDate,
          answeredFromDate,
          answeredToDate,
        });
        // API now returns the exact total, so pagination is precise.
        // A search with no matches can return data as null/undefined instead of
        // an empty array — guard so `reports` is never undefined (the render
        // reads reports.length).
        const items = Array.isArray(result?.data) ? result.data : [];
        const total = result?.totalRecords ?? 0;
        setReports(items);
        setTotalReports(total);
        setHasMorePages(currentPage * pageSize < total);
      } catch (error: any) {
        // Handle 204 No Content response (no more results)
        if (error?.response?.status === 204) {
          setReports([]);
          setHasMorePages(false);
          // If we're on page > 1 and get 204, we've reached the end
          if (currentPage > 1) {
            setTotalReports((currentPage - 1) * pageSize);
          } else {
            setTotalReports(0);
          }
        } else {
          console.error('Failed to load reports:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    loadReports();
  }, [currentPage, pageSize, searchTerm, requestedFromDate, requestedToDate, answeredFromDate, answeredToDate]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleApplyFilters = (filters: {
    requestedFromDate?: string;
    requestedToDate?: string;
    answeredFromDate?: string;
    answeredToDate?: string;
  }) => {
    setRequestedFromDate(filters.requestedFromDate);
    setRequestedToDate(filters.requestedToDate);
    setAnsweredFromDate(filters.answeredFromDate);
    setAnsweredToDate(filters.answeredToDate);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setRequestedFromDate(undefined);
    setRequestedToDate(undefined);
    setAnsweredFromDate(undefined);
    setAnsweredToDate(undefined);
    setCurrentPage(1);
  };

  const handleSelectReport = (reportId: number) => {
    setSelectedReports((prev) =>
      prev.includes(reportId) ? prev.filter((id) => id !== reportId) : [...prev, reportId]
    );
  };

  const sendWarning = async (recipients: Recipient[]): Promise<boolean> => {
    try {
      await sendWarningEmails({ recipients });
      showToast(
        `Warning email sent to ${recipients.length} recipient${recipients.length > 1 ? 's' : ''}`,
        'success'
      );
      return true;
    } catch (error) {
      console.error('Failed to send warning emails:', error);
      showToast('Failed to send warning emails. Please try again.', 'error');
      return false;
    }
  };

  const handleSendWarning = async (report: Report) => {
    if (sendingWarningIds.has(report.reportManagementId) || 
        sentWarningIds.has(report.reportManagementId)) {
      return;
    }

    setSendingWarningIds(prev => new Set(prev).add(report.reportManagementId));
    
    const success = await sendWarning([
      {
        reportManagementId: report.reportManagementId,
        name: report.answeredBy,
        email: report.responderEmail,
        flightNumber: report.flightNumber,
      },
    ]);

    setSendingWarningIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(report.reportManagementId);
      return newSet;
    });

    if (success) {
      setSentWarningIds(prev => new Set(prev).add(report.reportManagementId));
    }
  };

  const handleBulkSendWarning = async () => {
    if (selectedReports.length === 0) {
      showToast('Please select at least one report', 'error');
      return;
    }

    if (sendingBulkWarning) return;

    // Never re-warn a report that's already been warned (backend flag or sent
    // this session), matching the per-row button which is disabled when warned.
    const recipients = reports
      .filter(
        (report) =>
          selectedReports.includes(report.reportManagementId) &&
          !(report.isWarningSent || sentWarningIds.has(report.reportManagementId))
      )
      .map((report) => ({
        reportManagementId: report.reportManagementId,
        name: report.answeredBy,
        email: report.responderEmail,
        flightNumber: report.flightNumber,
      }));

    if (recipients.length === 0) {
      showToast('The selected report(s) have already been warned.', 'error');
      return;
    }

    setSendingBulkWarning(true);

    const success = await sendWarning(recipients);

    if (success) {
      setSentWarningIds(prev => {
        const newSet = new Set(prev);
        recipients.forEach((r) => newSet.add(r.reportManagementId));
        return newSet;
      });
      setSelectedReports([]);
    }

    setSendingBulkWarning(false);
  };

  const handleViewDetails = (report: Report) => {
    setSelectedReport(report);
    setIsModalOpen(true);
  };

  return (
    <section className="bg-white p-6 rounded-xl mt-6">
      <div className="mt-6">
        <div className="flex flex-wrap gap-4 items-center justify-between pb-4">
          <p className="text-grease-100 text-sm font-semibold">Report Management</p>
          <div className="flex flex-wrap items-center gap-3 relative">
            <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
            <button
              className="flex items-center justify-center gap-2 p-3 border bg-amber-500 rounded-lg"
              onClick={handleBulkSendWarning}
              disabled={sendingBulkWarning}
            >
              {sendingBulkWarning ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="font-medium text-white text-sm">Sending...</p>
                </>
              ) : (
                <>
                  <span className="st-warning text-white" />
                  <p className="font-medium text-white text-sm">Send Warning</p>
                </>
              )}
            </button>
            <div className="relative">
              <button
                id="dropdownDefaultButton-3"
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="flex items-center justify-center gap-2 p-3 border bg-[#FFD700] rounded-lg"
              >
                <i className="st-filter text-white"></i>
                <p className="font-medium text-white text-sm">Filters</p>
              </button>
              <Filter
                isOpen={isFilterOpen}
                onClose={() => setIsFilterOpen(false)}
                onApply={handleApplyFilters}
                onClear={handleClearFilters}
              />
            </div>
          </div>
        </div>

        <div className="relative overflow-x-auto sm:rounded-lg">
          <table className="w-full text-sm text-left text-gray-500">
            <thead className="text-xs text-gray-700 p-4 bg-blue-50">
              <tr>
                <th className="px-6 py-4">Sl.No.</th>
                <th className="px-6 py-4">Request By</th>
                <th className="px-6 py-4">Requested Date</th>
                <th className="px-6 py-4">Answered By</th>
                <th className="px-6 py-4">Answered Date</th>
                <th className="px-6 py-4"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center">Loading...</td>
                </tr>
              ) : (reports?.length ?? 0) > 0 ? (
                (reports ?? []).map((report, index) => {
                  const isSending = sendingWarningIds.has(report.reportManagementId);
                  // Warned if the backend flag is set, or we sent it this session.
                  const alreadyWarned =
                    report.isWarningSent || sentWarningIds.has(report.reportManagementId);
                  return (
                  <tr key={report.reportManagementId} className="odd:bg-white even:bg-blue-50 border">
                    <td className="px-6 py-4 flex items-center gap-4">
                      <input
                        type="checkbox"
                        checked={selectedReports.includes(report.reportManagementId)}
                        onChange={() => handleSelectReport(report.reportManagementId)}
                        disabled={alreadyWarned}
                        title={alreadyWarned ? "Already warned" : undefined}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed"
                      />
                      {(currentPage - 1) * pageSize + index + 1}
                    </td>
                    <td className="px-6 py-4">{report.requestedBy}</td>
                    <td className="px-6 py-4">{report.requestedDate}</td>
                    <td className="px-6 py-4">{report.answeredBy}</td>
                    <td className="px-6 py-4">{report.answeredDate}</td>
                    <td className="px-6 py-4 flex items-center justify-end gap-3">
                      <button
                        onClick={() => handleViewDetails(report)}
                        className="flex items-center gap-1 px-4 py-3 rounded-md border border-blue-300 text-blue-500 text-xs"
                      >
                        <span className="st-eye text-blue-500 text-xs" />
                        View Details
                      </button>
                      <button
                        onClick={() => handleSendWarning(report)}
                        disabled={isSending || alreadyWarned}
                        className={`flex items-center gap-1 px-4 py-3 rounded-md border text-xs ${
                          alreadyWarned
                            ? 'border-green-300 bg-green-50 text-green-600 cursor-not-allowed'
                            : isSending
                            ? 'border-blue-300 opacity-50 cursor-not-allowed text-gray-400'
                            : 'border-blue-300 text-amber-500'
                        }`}
                      >
                        {isSending ? (
                          <>
                            <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Sending...
                          </>
                        ) : alreadyWarned ? (
                          <>
                            <svg className="w-3 h-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                            Warned
                          </>
                        ) : (
                          <>
                            <span className="st-warning text-amber-500 text-xs" />
                            Send Warning
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center">No reports found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalItems={totalReports}
          onPageChange={handlePageChange}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
        />

        <ReportDetailsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          report={selectedReport}
        />
      </div>
    </section>
  );
};

export default ReportManagement;